/**
 * Step3SelectTemplate - with engagement visibility preferences
 */

import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
  PlusIcon,
  BriefcaseIcon,
  ArrowPathIcon,
  XMarkIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  FunnelIcon,
  DocumentArrowUpIcon,   // ← ADD
} from '@heroicons/react/24/outline';
 
import EngagementTable from '../../../bids/engagements/EngagementTable';
import EngagementBidIntelligence from '../../../bids/engagements/EngagementBidIntelligence';
import { useSyncAllActuals } from '../../../../hooks/engagement/useEngagement';
import EngagementFilters from '../../../bids/engagements/EngagementFilters';
import { useUserPreference } from '../../../../hooks/useUserPreference';

const fmt = (val) =>
  val != null
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(val)
    : '—';

const Step3SelectTemplate = ({
  wizardData,
  filteredEngagements,
  realEngagements,
  isLoadingEngagements,
  onUpdateFilter,
  onRefetchEngagements,
  onCreateEngagement,
  onEditEngagement,
  onSelectTemplate,
  onPreviewDocument,
  onDownloadDocument,
  onNextStep,
  saveStates = {},
}) => {
  const [isRefreshing,      setIsRefreshing]      = React.useState(false);
  const [viewingEngagement, setViewingEngagement] = React.useState(null);
  const [syncSummary,       setSyncSummary]       = React.useState(null);
  const [showFilters,       setShowFilters]       = React.useState(false);

   // Engagement-linked upload state
  const [uploadEngagementId,   setUploadEngagementId]   = React.useState(null);
  const [uploadEngagementName, setUploadEngagementName] = React.useState(null);
  const [showEngUploadModal,   setShowEngUploadModal]   = React.useState(false);
  const engFileInputRef = React.useRef(null);
 
  const handleUploadForEngagement = useCallback((engagementId, engagementName) => {
    setUploadEngagementId(engagementId);
    setUploadEngagementName(engagementName);
    setShowEngUploadModal(true);
  }, []);
 
  const handleEngFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (engFileInputRef.current) engFileInputRef.current.value = '';
    setShowEngUploadModal(false);
    // Navigate to the Documents tab with the file pre-queued
    // The simplest approach: open the full DocumentManagementTab upload flow
    // by dispatching a custom event the parent (BidCalculatorPage) can listen to.
    window.dispatchEvent(new CustomEvent('pulse:upload-for-engagement', {
      detail: { files, engagementId: uploadEngagementId, engagementName: uploadEngagementName }
    }));
  };

  const { mutateAsync: syncAll, isLoading: isSyncing } = useSyncAllActuals();
  const intelligenceRef = React.useRef(null);

  // ── Visibility preferences ───────────────────────────────────────────────
  // Persisted: array of hidden engagement IDs
  const [savedHiddenIds, saveHiddenIds, isPrefLoading] = useUserPreference(
    'bid_template',
    'hidden_engagements',
    [],
  );

  // Committed (applied) hidden set — updated only on Save
  const [committedHiddenIds, setCommittedHiddenIds] = React.useState(null);

  // Once preferences load from backend, seed committed state
  React.useEffect(() => {
    if (!isPrefLoading && committedHiddenIds === null) {
      const ids = Array.isArray(savedHiddenIds)
        ? savedHiddenIds                          // legacy plain array
        : savedHiddenIds?.hidden_ids ?? [];       // new dict shape
      setCommittedHiddenIds(new Set(ids));
    }
  }, [isPrefLoading, savedHiddenIds, committedHiddenIds]);

  // Local (unsaved) state for the panel checkboxes
  const [localHiddenIds, setLocalHiddenIds] = React.useState(null);

  // Sync local state whenever committed state is first seeded or reset
  React.useEffect(() => {
    if (committedHiddenIds !== null && localHiddenIds === null) {
      setLocalHiddenIds(new Set(committedHiddenIds));
    }
  }, [committedHiddenIds, localHiddenIds]);

  const [isSavingVisibility, setIsSavingVisibility] = React.useState(false);

  const hiddenIds = committedHiddenIds ?? new Set();
  const localIds  = localHiddenIds    ?? new Set();

  const hasUnsavedVisibility = React.useMemo(() => {
    if (!committedHiddenIds || !localHiddenIds) return false;
    if (committedHiddenIds.size !== localHiddenIds.size) return true;
    for (const id of localHiddenIds) {
      if (!committedHiddenIds.has(id)) return true;
    }
    return false;
  }, [committedHiddenIds, localHiddenIds]);

  const handleToggleHidden = useCallback((id) => {
    setLocalHiddenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSelectAllHidden = useCallback(() => {
    setLocalHiddenIds((prev) => {
      // If anything is hidden, show all; otherwise hide all
      if (prev.size > 0) return new Set();
      return new Set((realEngagements ?? []).map((e) => e.id));
    });
  }, [realEngagements]);

  const handleSaveVisibility = useCallback(async () => {
    if (!localHiddenIds) return;
    setIsSavingVisibility(true);
    try {
      const idsArray = Array.from(localHiddenIds);
      await saveHiddenIds({ hidden_ids: idsArray });
      setCommittedHiddenIds(new Set(localHiddenIds));
    } catch (err) {
      console.error('Failed to save visibility preferences:', err);
    } finally {
      setIsSavingVisibility(false);
    }
  }, [localHiddenIds, saveHiddenIds]);

  const handleCancelVisibility = useCallback(() => {
    // Reset local state back to what's committed
    setLocalHiddenIds(new Set(committedHiddenIds));
  }, [committedHiddenIds]);

  // ── Other handlers ───────────────────────────────────────────────────────
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try { await onRefetchEngagements(); }
    finally { setIsRefreshing(false); }
  };

  const handleSyncActuals = async () => {
    setSyncSummary(null);
    try {
      const response = await syncAll();
      setSyncSummary(response?.data);
      await onRefetchEngagements();
    } catch (err) {
      console.error('Sync actuals failed:', err);
    }
  };

  const handleResetFilters = () => {
    onUpdateFilter('searchClient',   '');
    onUpdateFilter('industry',       '');
    onUpdateFilter('practiceArea',   '');
    onUpdateFilter('tags',           []);
    onUpdateFilter('timePeriod',     'all');
    onUpdateFilter('startDate',      null);
    onUpdateFilter('endDate',        null);
    onUpdateFilter('excludeUnknown', false);
  };

  const hasActiveFilters =
    wizardData.filters?.searchClient ||
    wizardData.filters?.industry ||
    wizardData.filters?.practiceArea ||
    wizardData.filters?.tags?.length > 0 ||
    (wizardData.filters?.timePeriod && wizardData.filters.timePeriod !== 'all') ||
    wizardData.filters?.excludeUnknown;

  const lastSyncedAt = React.useMemo(() => {
    if (!realEngagements?.length) return null;
    const timestamps = realEngagements
      .map((e) => e.last_sync_at)
      .filter(Boolean)
      .sort()
      .reverse();
    if (!timestamps.length) return null;
    return new Date(timestamps[0]).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  }, [realEngagements]);

  {/* ── Engagement-linked upload modal ── */}
      {showEngUploadModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full">
            <div className="flex items-center justify-between px-5 py-4 border-b border-grey-200">
              <div>
                <h3 className="text-sm font-semibold text-darkblue-500">Upload Document</h3>
                {uploadEngagementName && (
                  <p className="text-xs text-grey-500 mt-0.5 truncate max-w-[220px]">
                    → {uploadEngagementName}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowEngUploadModal(false)}
                className="p-1.5 text-grey-400 hover:bg-grey-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-xs text-grey-500 mb-4">
                The document will be automatically linked to this engagement and extracted with AI.
              </p>
              <input
                ref={engFileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleEngFileSelect}
                className="hidden"
              />
              <button
                onClick={() => engFileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-darkblue-500 text-white rounded-lg hover:bg-darkblue-600 transition-colors text-sm font-medium"
              >
                <DocumentArrowUpIcon className="w-4 h-4" />
                Select File
              </button>
            </div>
            <div className="px-5 py-3 bg-grey-50 border-t border-grey-200 rounded-b-xl">
              <button
                onClick={() => setShowEngUploadModal(false)}
                className="text-xs text-grey-500 hover:text-grey-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

  return (
    <div className="space-y-4">

      {/* ── Header row ── */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Select Reference Engagement</h2>
          <p className="text-xs text-gray-500 mt-0.5">Choose a Won engagement as your starting template</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Filters</span>
            {hasActiveFilters && (
              <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-blue-500" />
            )}
          </button>

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={isLoadingEngagements || isRefreshing || isSyncing}
            title="Refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors disabled:opacity-40"
          >
            <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{isRefreshing ? 'Loading...' : 'Refresh'}</span>
          </button>

          {/* Create */}
          <button
            onClick={onCreateEngagement}
            disabled={isLoadingEngagements}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brightblue-600 text-white hover:bg-brightblue-700 text-sm font-medium transition-colors disabled:opacity-50"
          >
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:inline">New</span>
          </button>
        </div>
      </div>

      {/* ── Filters (collapsible) ── */}
      {showFilters && (
        <EngagementFilters
          filters={wizardData.filters}
          realEngagements={realEngagements}
          onUpdateFilter={onUpdateFilter}
          isLoadingEngagements={isLoadingEngagements}
          onResetFilters={handleResetFilters}
        />
      )}

      {/* ── Sync summary banner ── */}
      {syncSummary && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg text-sm">
          <span className="font-semibold text-green-800">Sync complete</span>
          <span className="text-green-700">✅ {syncSummary.synced} synced</span>
          {syncSummary.skipped > 0 && <span className="text-amber-700">⏭ {syncSummary.skipped} skipped</span>}
          {syncSummary.errors  > 0 && <span className="text-red-700">❌ {syncSummary.errors} errors</span>}
          {syncSummary.synced_at && (
            <span className="text-gray-400 ml-auto text-xs">
              {new Date(syncSummary.synced_at).toLocaleString('en-US', {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
              })}
            </span>
          )}
        </div>
      )}

      {/* ── Table card ── */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">

        {/* Meta row */}
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-2">
          <p className="text-xs text-gray-500">
            {isLoadingEngagements ? 'Loading...' : (
              <>
                <span className="font-semibold text-gray-700">
                  {filteredEngagements.filter((e) => !hiddenIds.has(e.id)).length}
                </span>
                {realEngagements.length !== filteredEngagements.length && (
                  <span className="text-gray-400"> of {realEngagements.length}</span>
                )}
                {' '}Won engagement{filteredEngagements.length !== 1 ? 's' : ''}
                {hasActiveFilters && <span className="text-blue-600 ml-1">(filtered)</span>}
                {hiddenIds.size > 0 && (
                  <span className="text-gray-400 ml-1">· {hiddenIds.size} hidden</span>
                )}
              </>
            )}
          </p>
          <div className="flex items-center gap-3">
            {hasActiveFilters && (
              <button
                onClick={handleResetFilters}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <XMarkIcon className="w-3 h-3" />
                Clear filters
              </button>
            )}
            {lastSyncedAt && (
              <p className="text-xs text-gray-400 hidden sm:block">Synced {lastSyncedAt}</p>
            )}
          </div>
        </div>

        {/* Content */}
        {isLoadingEngagements ? (
          <div className="py-16 text-center">
            <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">Loading Won engagements...</p>
          </div>
        ) : filteredEngagements.length === 0 && hiddenIds.size === 0 ? (
          <div className="py-16 text-center px-6">
            <BriefcaseIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-gray-900 mb-1">No Won Engagements Found</h3>
            <p className="text-xs text-gray-500 mb-5">
              {realEngagements.length > 0
                ? 'Try adjusting your filters'
                : 'Create your first Won engagement to build historical intelligence'}
            </p>
            <button
              onClick={onCreateEngagement}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
            >
              <PlusIcon className="w-4 h-4" />
              Create First Engagement
            </button>
          </div>
        ) : (
          <div data-tour="step3-engagement-table">
            <EngagementTable
              engagements={filteredEngagements}
              allEngagements={realEngagements}
              filters={wizardData.filters}
              onUpdateFilter={onUpdateFilter}
              onResetFilters={handleResetFilters}
              onSelectTemplate={onSelectTemplate}
              onEditEngagement={onEditEngagement}
              onPreviewDocument={onPreviewDocument}
              onDownloadDocument={onDownloadDocument}
              onNextStep={onNextStep}
              onViewBreakdown={(eng) => setViewingEngagement(eng)}
              saveStates={saveStates}
              onUploadForEngagement={handleUploadForEngagement}   // ← ADD
              // Visibility props
              hiddenIds={hiddenIds}
              localHiddenIds={localIds}
              onToggleHidden={handleToggleHidden}
              onSelectAllHidden={handleSelectAllHidden}
              onSaveVisibility={handleSaveVisibility}
              onCancelVisibility={handleCancelVisibility}
              hasUnsavedVisibility={hasUnsavedVisibility}
              isSavingVisibility={isSavingVisibility}
            />
          </div>
        )}
      </div>

      {/* ── Bid Intelligence Modal ── */}
      {viewingEngagement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setViewingEngagement(null)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3.5 flex items-start justify-between z-10">
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm">
                  <ChartBarIcon className="w-4 h-4 text-blue-600" />
                  {viewingEngagement.engagement_name || viewingEngagement.customer_name}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {[viewingEngagement.engagement_type, viewingEngagement.industry, viewingEngagement.qb_customer_name]
                    .filter(Boolean).join(' · ')}
                </p>
              </div>
              <button
                onClick={() => setViewingEngagement(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors ml-4"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Stats */}
            <div className="px-5 py-3 grid grid-cols-3 gap-2.5 border-b border-gray-100">
              <div className="p-2.5 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-0.5 flex items-center gap-1">
                  <CurrencyDollarIcon className="w-3 h-3" /> Bid Amount
                </p>
                <p className="text-sm font-bold text-slate-900">
                  {fmt(viewingEngagement.bid_amount || viewingEngagement.estimated_value)}
                </p>
              </div>
              <div className="p-2.5 bg-green-50 rounded-lg">
                <p className="text-xs text-green-600 mb-0.5">Actual Rev</p>
                <p className="text-sm font-bold text-green-900">{fmt(viewingEngagement.actual_revenue)}</p>
              </div>
              <div className="p-2.5 bg-purple-50 rounded-lg">
                <p className="text-xs text-purple-600 mb-0.5">Margin</p>
                <p className="text-sm font-bold text-purple-900">
                  {viewingEngagement.profit_margin != null
                    ? `${viewingEngagement.profit_margin.toFixed(1)}%` : '—'}
                </p>
              </div>
            </div>

            {/* Scope */}
            {viewingEngagement.scope_summary && (
              <div className="px-5 py-3 border-b border-gray-100">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Scope</p>
                <p className="text-sm text-slate-700">{viewingEngagement.scope_summary}</p>
              </div>
            )}

            {/* Document */}
            {viewingEngagement.document_id && (
              <div className="px-5 py-2.5 border-b border-gray-100 flex items-center gap-2">
                <DocumentTextIcon className="w-4 h-4 text-blue-500 shrink-0" />
                <span className="text-sm text-slate-600 truncate">
                  {viewingEngagement.document_name || 'Proposal document'}
                </span>
                {onPreviewDocument && (
                  <button
                    onClick={() => onPreviewDocument(viewingEngagement.document_id)}
                    className="ml-auto text-xs text-blue-600 hover:underline shrink-0"
                  >
                    Preview →
                  </button>
                )}
              </div>
            )}

            {/* Intelligence */}
            <div className="px-5 py-4">
              <EngagementBidIntelligence ref={intelligenceRef} engagement={viewingEngagement} />
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-5 py-3 flex justify-between gap-3">
              <button
                onClick={() => setViewingEngagement(null)}
                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  const filteredComponents = intelligenceRef.current?.getFilteredComponents() || [];
                  onSelectTemplate(viewingEngagement, filteredComponents);
                  setViewingEngagement(null);
                  onNextStep();
                }}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Use as Template →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step3SelectTemplate;